import {
  CommunityMemberMessagingMode,
  CommunityMembershipRole,
  CommunityMembershipStatus,
} from '../enums';
import { CommunityModeratorPermission, hasCommunityModeratorPermission } from './community-moderator-permissions.policy';

export type CommunityDirectMessageMembership = {
  userId: string;
  role: string;
  status: string;
  moderatorPermissions?: string[] | null;
};

export type CommunityDirectMessageContext = {
  messagingMode: CommunityMemberMessagingMode | string;
  actorMembership: CommunityDirectMessageMembership;
  targetMembership: CommunityDirectMessageMembership;
};

export function canInitiateCommunityMemberDm(params: CommunityDirectMessageContext):
  | { ok: true }
  | { ok: false; message: string } {
  if (params.actorMembership.userId === params.targetMembership.userId) {
    return { ok: false, message: 'You cannot message yourself' };
  }

  if (params.actorMembership.status !== CommunityMembershipStatus.ACTIVE) {
    return { ok: false, message: 'Active Circle membership required' };
  }

  if (params.targetMembership.status !== CommunityMembershipStatus.ACTIVE) {
    return { ok: false, message: 'Target member is not active in this Circle' };
  }

  const membersAllowed =
    params.messagingMode === CommunityMemberMessagingMode.MEMBERS_ALLOWED;

  if (membersAllowed) {
    return { ok: true };
  }

  if (params.targetMembership.role === CommunityMembershipRole.OWNER) {
    return { ok: false, message: 'Circle owners cannot be messaged through this action' };
  }

  const actorIsOwner = params.actorMembership.role === CommunityMembershipRole.OWNER;
  const actorIsModerator =
    params.actorMembership.role === CommunityMembershipRole.MODERATOR &&
    hasCommunityModeratorPermission(params.actorMembership, CommunityModeratorPermission.MANAGE_MEMBERS);

  if (!actorIsOwner && !actorIsModerator) {
    return { ok: false, message: 'Only Circle owners and member moderators can message members here' };
  }

  return { ok: true };
}

export function shouldBypassFollowGateForCommunityDm(
  communityPolicy: { ok: true } | { ok: false; message: string },
): boolean {
  return communityPolicy.ok;
}

export function communityMemberCanSeeMessageAction(params: {
  messagingMode: CommunityMemberMessagingMode | string;
  viewerUserId: string;
  targetUserId: string;
  viewerRole?: string | null;
  targetRole?: string | null;
  viewerCanManageMembers: boolean;
  targetBlocked: boolean;
}): boolean {
  if (params.viewerUserId === params.targetUserId || params.targetBlocked) return false;

  if (params.messagingMode === CommunityMemberMessagingMode.MEMBERS_ALLOWED) {
    return true;
  }

  return (
    params.viewerCanManageMembers &&
    params.targetRole !== CommunityMembershipRole.OWNER
  );
}
